import type { TimedMusicXmlNote } from "./songEngine";
import type { SongPlaybackTimeline } from "./songPlayback";
import {
  computeAdaptiveNoteWindows,
  DEFAULT_PITCH_LATENCY_MS,
} from "./tempoAdaptiveTiming";
import type { PracticePlaybackEvent } from "./practiceEventScoring";

export interface PracticePitchFrame {
  tMs: number;
  midi: number;
  confidence: number;
  volume: number;
  cents: number;
}

export type PracticeNoteStatus = "pending" | "hit" | "miss" | "late";

export interface PracticeNoteScore {
  noteIndex: number;
  /** Instante musical (divisions) — agrupa acordes simultáneos. */
  attackTime: number;
  expectedMidi: number;
  startMs: number;
  endMs: number;
  durationMs: number;
  earlyMs: number;
  lateMs: number;
  sustainTailMs: number;
  lateGraceMs: number;
  status: PracticeNoteStatus;
  bestFrame: PracticePitchFrame | null;
  timingErrorMs: number | null;
  cents: number | null;
}

export interface PracticeScoringSnapshot {
  total: number;
  hit: number;
  miss: number;
  late: number;
  /** Notas en eventos polifónicos con cobertura parcial (algunas voces acertadas). */
  partial: number;
  pending: number;
  accuracy: number;
  currentStatus: "match" | "miss" | "partial" | "idle";
  eventTotal: number;
  eventHit: number;
  eventPartial: number;
  eventMiss: number;
}

export interface PracticeScoringOptions {
  minConfidence?: number;
  minVolume?: number;
  maxCents?: number;
  acceptOctaveCorrection?: boolean;
  /** Resta este offset al tiempo del frame (compensa latencia del mic). */
  latencyOffsetMs?: number;
  /** Prioriza la nota activa en el playhead. */
  currentNoteIndex?: number;
}

const DEFAULT_OPTIONS: Required<
  Pick<
    PracticeScoringOptions,
    "minConfidence" | "minVolume" | "maxCents" | "acceptOctaveCorrection" | "latencyOffsetMs"
  >
> = {
  minConfidence: 0.28,
  minVolume: 0.0018,
  maxCents: 54,
  acceptOctaveCorrection: true,
  latencyOffsetMs: DEFAULT_PITCH_LATENCY_MS,
};

export function pitchMatchesExpected(
  heardMidi: number,
  expectedMidi: number,
  cents: number,
  acceptOctaveCorrection: boolean,
  maxCents: number
): boolean {
  const heard = Math.round(heardMidi);
  const expected = Math.round(expectedMidi);
  if (Math.abs(cents) > maxCents) return false;
  if (heard === expected) return true;
  if (!acceptOctaveCorrection) return false;
  if (heard % 12 !== expected % 12) return false;
  return Math.abs(Math.abs(heard - expected) - 12) <= 0;
}

function windowEndMs(score: PracticeNoteScore): number {
  return Math.max(score.startMs + score.lateMs, score.endMs + score.sustainTailMs);
}

function hardEndMs(score: PracticeNoteScore): number {
  return windowEndMs(score) + score.lateGraceMs;
}

function resolveOptions(opts: PracticeScoringOptions = {}) {
  return { ...DEFAULT_OPTIONS, ...opts };
}

function effectiveFrameMs(frame: PracticePitchFrame, latencyOffsetMs: number): number {
  return Math.max(0, frame.tMs - latencyOffsetMs);
}

export function buildPracticeNoteScores(
  notes: TimedMusicXmlNote[],
  timeline: SongPlaybackTimeline
): PracticeNoteScore[] {
  const segmentByStart = new Map(
    timeline.segments.map((s) => [s.startTime, s] as const)
  );
  const raw = notes
    .map((note, noteIndex): PracticeNoteScore | null => {
      if (note.isRest || note.midi <= 0) return null;
      const segment = segmentByStart.get(note.startTime);
      if (!segment) return null;
      const durationMs = Math.max(60, segment.endMs - segment.startMs);
      return {
        noteIndex,
        attackTime: note.startTime,
        expectedMidi: note.midi,
        startMs: segment.startMs,
        endMs: segment.endMs,
        durationMs,
        earlyMs: 0,
        lateMs: 0,
        sustainTailMs: 0,
        lateGraceMs: 0,
        status: "pending",
        bestFrame: null,
        timingErrorMs: null,
        cents: null,
      };
    })
    .filter((score): score is PracticeNoteScore => score !== null);

  const byStart = [...raw].sort((a, b) => a.startMs - b.startMs);
  for (let i = 0; i < byStart.length; i++) {
    const score = byStart[i];
    const next = byStart[i + 1];
    const ioiMs = next ? next.startMs - score.startMs : score.durationMs;
    const windows = computeAdaptiveNoteWindows(score.durationMs, ioiMs);
    score.earlyMs = windows.earlyMs;
    score.lateMs = windows.lateMs;
    score.sustainTailMs = windows.sustainTailMs;
    score.lateGraceMs = windows.lateGraceMs;
  }

  return raw;
}

/**
 * Elige la nota pendiente que mejor encaja con el frame.
 * En acordes (mismo attackTime), prioriza el evento activo y la altura oída.
 */
function pickBestCandidate(
  allScores: PracticeNoteScore[],
  candidates: PracticeNoteScore[],
  tMs: number,
  frameMidi: number,
  currentNoteIndex?: number
): PracticeNoteScore | null {
  if (candidates.length === 0) return null;

  const anchor =
    currentNoteIndex !== undefined
      ? allScores.find((s) => s.noteIndex === currentNoteIndex)
      : undefined;

  const currentAttack = anchor?.attackTime;

  const sameAttack =
    currentAttack !== undefined
      ? candidates.filter((c) => c.attackTime === currentAttack)
      : [];

  const pool = sameAttack.length > 0 ? sameAttack : candidates;

  const heard = Math.round(frameMidi);
  const exactMidi = pool.filter((c) => Math.round(c.expectedMidi) === heard);
  const pool2 = exactMidi.length > 0 ? exactMidi : pool;

  let best: PracticeNoteScore | null = null;
  let bestKey = Infinity;

  for (const score of pool2) {
    const absTiming = Math.abs(tMs - score.startMs);
    const midiDist = Math.abs(Math.round(score.expectedMidi) - heard);
    const urgency = windowEndMs(score) - tMs;
    const key = absTiming * 1.2 + midiDist * 8 + Math.max(0, urgency) * 0.3 + score.noteIndex * 0.01;
    if (key < bestKey) {
      best = score;
      bestKey = key;
    }
  }

  return best;
}

export function scorePracticeFrame(
  scores: PracticeNoteScore[],
  frame: PracticePitchFrame,
  opts: PracticeScoringOptions = {}
): boolean {
  const options = resolveOptions(opts);
  if (frame.confidence < options.minConfidence || frame.volume < options.minVolume) {
    return false;
  }

  const tMs = effectiveFrameMs(frame, options.latencyOffsetMs);
  const candidates: PracticeNoteScore[] = [];

  for (const score of scores) {
    if (score.status !== "pending") continue;
    if (tMs < score.startMs - score.earlyMs) continue;
    if (tMs > hardEndMs(score)) continue;

    const matched = pitchMatchesExpected(
      frame.midi,
      score.expectedMidi,
      frame.cents,
      options.acceptOctaveCorrection,
      options.maxCents
    );
    if (!matched) continue;
    candidates.push(score);
  }

  const best = pickBestCandidate(scores, candidates, tMs, frame.midi, options.currentNoteIndex);
  if (!best) return false;

  const end = windowEndMs(best);
  const isLate = tMs > end;

  best.status = isLate ? "late" : "hit";
  best.bestFrame = frame;
  best.timingErrorMs = tMs - best.startMs;
  best.cents = frame.cents;
  return true;
}

export function finalizeExpiredPracticeScores(
  scores: PracticeNoteScore[],
  playbackMs: number
): boolean {
  let changed = false;

  for (const score of scores) {
    if (score.status !== "pending") continue;
    if (playbackMs > hardEndMs(score)) {
      score.status = "miss";
      changed = true;
    }
  }

  return changed;
}

export function summarizePracticeScores(
  scores: PracticeNoteScore[],
  currentNoteIndex: number,
  events?: PracticePlaybackEvent[]
): PracticeScoringSnapshot {
  const total = scores.length;
  const hit = scores.filter((s) => s.status === "hit").length;
  const miss = scores.filter((s) => s.status === "miss").length;
  const late = scores.filter((s) => s.status === "late").length;
  const pending = scores.filter((s) => s.status === "pending").length;

  let partial = 0;
  let eventTotal = 0;
  let eventHit = 0;
  let eventPartial = 0;
  let eventMiss = 0;

  if (events && events.length > 0) {
    eventTotal = events.length;
    eventHit = events.filter((e) => e.status === "hit" || e.status === "late").length;
    eventPartial = events.filter((e) => e.status === "partial").length;
    eventMiss = events.filter((e) => e.status === "miss").length;

    for (const ev of events) {
      if (ev.status !== "partial") continue;
      for (const idx of ev.noteIndices) {
        const s = scores.find((n) => n.noteIndex === idx);
        if (s && s.status === "miss") partial += 1;
      }
    }
  }

  const current = scores.find((s) => s.noteIndex === currentNoteIndex);
  const currentEvent = events?.find((e) => e.noteIndices.includes(currentNoteIndex));

  let currentStatus: PracticeScoringSnapshot["currentStatus"] = "idle";
  if (current?.status === "hit" || current?.status === "late") {
    currentStatus = "match";
  } else if (current?.status === "miss") {
    currentStatus = "miss";
  } else if (currentEvent?.status === "partial") {
    currentStatus = "partial";
  }

  const credited = hit + late + partial * 0.65;

  return {
    total,
    hit,
    miss,
    late,
    partial,
    pending,
    accuracy: total > 0 ? Math.min(1, credited / total) : 0,
    currentStatus,
    eventTotal,
    eventHit,
    eventPartial,
    eventMiss,
  };
}
